import React, { useState, useEffect, useRef, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import { 
  Map as MapIcon, 
  Search, 
  Plus, 
  Edit2, 
  Trash2, 
  X, 
  Navigation,
  Layers,
  Info
} from 'lucide-react';
import Loader from '../../components/Loader/Loader';
import './geofence.css';

// Set Mapbox access token
mapboxgl.accessToken = 'pk.eyJ1IjoiYWxjb3Jha2UiLCJhIjoiY2tydnJub2JpMDllajJwb3h2NjdjbjFkdSJ9.6niQ3lJJ1OVEBMKj_BvFkA';

const Geofence = () => {
  const [geofences, setGeofences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [geofenceName, setGeofenceName] = useState('');
  const [editingIndex, setEditingIndex] = useState(null);
  const [loadedEntity, setLoadedEntity] = useState(null);
  const [isGeofenceActive, setIsGeofenceActive] = useState(false);
  
  const [newEntity, setNewEntity] = useState({
    id: '',
    name: '',
    lon: '',
    lat: '',
    radius: '5000'
  });

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const showGeofenceAlert = (options) => Swal.fire({
    ...options,
    customClass: {
      ...options.customClass,
      container: 'geofence-swal-container'
    }
  });

  useEffect(() => {
    fetchGeofenceData();
    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
      }
    };
  }, []);

  const fetchGeofenceData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`https://alcobrakeadmin.com/v3/us/geofence/getdealergeofences?dealerId=${user.dealerId}`);
      if (response.data && response.data.length > 0) {
        const res = response.data[0];
        setLoadedEntity(res);
        setGeofences(JSON.parse(res.geometry || '[]'));
        setGeofenceName(res.name);
        setIsGeofenceActive(true);
      } else {
        setIsGeofenceActive(false);
      }
    } catch (error) {
      console.error('Error fetching geofence data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isGeofenceActive && mapContainerRef.current && !mapRef.current) {
      initMap();
    }
  }, [isGeofenceActive]);

  useEffect(() => {
    if (mapRef.current && mapRef.current.isStyleLoaded()) {
      renderGeofencesOnMap();
    }
  }, [geofences]);

  const initMap = () => {
    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: document.body.getAttribute('data-theme') === 'dark'
        ? 'mapbox://styles/mapbox/dark-v11'
        : 'mapbox://styles/mapbox/streets-v11',
      center: [
        parseFloat(geofences[0]?.lon) || -98.5795, // US Center if no data
        parseFloat(geofences[0]?.lat) || 39.8283
      ],
      zoom: 9,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    const geocoder = new MapboxGeocoder({
      accessToken: mapboxgl.accessToken,
      mapboxgl: mapboxgl,
      placeholder: 'Search location...',
      marker: false,
      zoom: 13
    });

    map.addControl(geocoder, 'top-left');

    map.on('load', () => {
      mapRef.current = map;
      renderGeofencesOnMap();

      map.on('click', (e) => {
        const { lng, lat } = e.lngLat;
        openAddDialog(lng, lat);
      });
    });

    geocoder.on('result', (e) => {
      const [lon, lat] = e.result.center;
      openAddDialog(lon, lat);
    });
  };

  const createCircle = (center, radiusMeters) => {
    const points = 64;
    const km = radiusMeters / 1000;
    const ret = [];
    const distanceX = km / (111.32 * Math.cos(center[1] * Math.PI / 180));
    const distanceY = km / 110.574;

    for (let i = 0; i < points; i++) {
      const theta = (i / points) * (2 * Math.PI);
      const x = distanceX * Math.cos(theta);
      const y = distanceY * Math.sin(theta);
      ret.push([center[0] + x, center[1] + y]);
    }
    ret.push(ret[0]);
    return ret;
  };

  const prevCountRef = useRef(0);

  const renderGeofencesOnMap = () => {
    const map = mapRef.current;
    if (!map) return;

    // Clear old markers
    markersRef.current.forEach(m => m.remove());
    markersRef.current = [];

    // Clear ALL possible old layers and sources (using the previous count to be sure)
    // We use a safe upper bound or track the exact count
    for (let i = 0; i < Math.max(geofences.length + 10, prevCountRef.current + 10); i++) {
      const layerId = `circle-layer-${i}`;
      const sourceId = `circle-source-${i}`;
      
      // Remove border layer first (important for Mapbox dependencies)
      if (map.getLayer(`${layerId}-border`)) map.removeLayer(`${layerId}-border`);
      if (map.getLayer(layerId)) map.removeLayer(layerId);
      if (map.getSource(sourceId)) map.removeSource(sourceId);
    }

    // Draw new geofences
    geofences.forEach((g, index) => {
      const lon = parseFloat(g.lon);
      const lat = parseFloat(g.lat);
      const radius = parseFloat(g.radius);

      if (isNaN(lon) || isNaN(lat) || isNaN(radius)) return;

      const sourceId = `circle-source-${index}`;
      const layerId = `circle-layer-${index}`;

      map.addSource(sourceId, {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: [createCircle([lon, lat], radius)]
          }
        }
      });

      map.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        paint: {
          'fill-color': '#4318ff',
          'fill-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.05,
            8.5, 0.02,
            9.5, 0
          ]
        }
      });

      // Add border layer
      map.addLayer({
        id: `${layerId}-border`,
        type: 'line',
        source: sourceId,
        paint: {
          'line-color': '#4318ff',
          'line-width': [
            'interpolate',
            ['linear'],
            ['zoom'],
            8, 1,
            9, 1,
            10, 0
          ],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            7, 0.7,
            8.5, 0.35,
            9.5, 0
          ]
        }
      });

      const marker = new mapboxgl.Marker({ color: '#4318ff' })
        .setLngLat([lon, lat])
        .addTo(map);

      const popup = new mapboxgl.Popup({ offset: 25 })
        .setHTML(`
          <div style="padding: 10px; font-family: Inter, sans-serif;">
            <strong style="display: block; margin-bottom: 5px; color: #1b2559;">${g.name || 'Unnamed'}</strong>
            <span style="color: #64748b; font-size: 12px;">Radius: ${(radius/1000).toFixed(1)} km</span>
          </div>
        `);

      marker.setPopup(popup);
      
      marker.getElement().addEventListener('click', (e) => {
        e.stopPropagation();
        handleEditClick(index);
      });

      markersRef.current.push(marker);
    });

    prevCountRef.current = geofences.length;
  };

  const openAddDialog = (lng, lat) => {
    // Check if duplicate exists
    const duplicate = geofences.find(g => 
      parseFloat(g.lat).toFixed(6) === lat.toFixed(6) && 
      parseFloat(g.lon).toFixed(6) === lng.toFixed(6)
    );

    if (duplicate) {
      showGeofenceAlert({ title: 'Error', text: 'A geofence already exists at this location!', icon: 'error' });
      return;
    }

    setEditingIndex(null);
    setNewEntity({
      id: '',
      name: '',
      lon: lng.toFixed(6),
      lat: lat.toFixed(6),
      radius: '5000'
    });
    setIsModalOpen(true);
  };

  const handleEditClick = (index) => {
    const g = geofences[index];
    setEditingIndex(index);
    setNewEntity({
      id: g.id || '',
      name: g.name,
      lon: g.lon,
      lat: g.lat,
      radius: g.radius
    });
    setIsModalOpen(true);
  };

  const handleDeleteClick = (item) => {
    showGeofenceAlert({
      title: 'Are you sure?',
      text: `Do you want to delete "${item.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#ee5d50',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        deleteGeofence(item);
      }
    });
  };

  const deleteGeofence = async (item) => {
    try {
      const deleteUrl = `https://alcobrakeadmin.com/v3/us/geofence/deletepaticuargeometry/${loadedEntity.id}/${item.name}`;
      await axios.get(deleteUrl);
      
      const newGeofences = geofences.filter(g => g.name !== item.name);
      setGeofences(newGeofences);
      
      showGeofenceAlert({ title: 'Deleted!', text: 'Geofence has been deleted.', icon: 'success' });
    } catch (error) {
      console.error('Delete error:', error);
      showGeofenceAlert({ title: 'Error', text: 'Failed to delete geofence.', icon: 'error' });
    }
  };

  const saveEntity = async () => {
    if (!newEntity.name || !newEntity.radius) {
      showGeofenceAlert({ title: 'Error', text: 'Name and Radius are required', icon: 'error' });
      return;
    }

    if (parseFloat(newEntity.radius) < 5000) {
      showGeofenceAlert({ title: 'Error', text: 'Minimum radius is 5000 meters', icon: 'error' });
      return;
    }

    let updatedGeofences = [...geofences];
    const entityToSave = {
      ...newEntity,
      lon: parseFloat(newEntity.lon).toFixed(3).toString(),
      lat: parseFloat(newEntity.lat).toFixed(3).toString(),
    };

    if (editingIndex !== null) {
      updatedGeofences[editingIndex] = entityToSave;
    } else {
      const nextId = geofences.length > 0 
        ? Math.max(...geofences.map(g => Number(g.id) || 0)) + 1 
        : 1;
      updatedGeofences.push({ ...entityToSave, id: nextId });
    }

    const formData = new FormData();
    formData.append('dealerId', user.dealerId);
    formData.append('name', loadedEntity?.name || geofenceName);
    formData.append('geometry', JSON.stringify(updatedGeofences));
    formData.append('metadata', JSON.stringify({
      created_at: new Date().toISOString(),
      created_by: user.userName || 'Admin'
    }));

    const url = loadedEntity?.id 
      ? `https://alcobrakeadmin.com/v3/us/geofence/editgeofence/${loadedEntity.id}`
      : 'https://alcobrakeadmin.com/v3/us/geofence/addgeofence';

    try {
      const response = await axios.post(url, formData);
      if (response.data) {
        setIsModalOpen(false);
        fetchGeofenceData();
        showGeofenceAlert({ title: 'Success', text: 'Geofence saved successfully!', icon: 'success' });
      }
    } catch (error) {
      console.error('Save error:', error);
      showGeofenceAlert({ title: 'Error', text: 'Failed to save geofence.', icon: 'error' });
    }
  };

  const startGeofenceCreation = () => {
    if (!geofenceName) {
      showGeofenceAlert({ title: 'Error', text: 'Please enter a geofence name', icon: 'error' });
      return;
    }
    setGeofences([]);
    setIsGeofenceActive(true);
    setLoadedEntity({
      id: null,
      name: geofenceName,
      dealerId: user.dealerId,
      geometry: '[]'
    });
    setIsNameModalOpen(false);
  };

  const filteredGeofences = useMemo(() => {
    if (!search) return geofences;
    const term = search.toLowerCase();
    return geofences.filter(g => 
      g.name?.toLowerCase().includes(term) ||
      g.lat?.toString().includes(term) ||
      g.lon?.toString().includes(term)
    );
  }, [search, geofences]);

  const handleFlyTo = (g) => {
    if (mapRef.current) {
      mapRef.current.flyTo({
        center: [parseFloat(g.lon), parseFloat(g.lat)],
        zoom: 13,
        essential: true
      });
    }
  };

  if (loading && !isGeofenceActive) {
    return (
      <div className="geofence-loading">
        <Loader />
        <p>Loading Geofencing System...</p>
      </div>
    );
  }

  return (
    <div className="geofence-container">
      <div className="geofence-card">
        <div className="geofence-header">
          <div className="header-left">
            <div className="header-icon">
              <MapIcon size={24} />
            </div>
            <div>
              <h1 className="header-title">
                {isGeofenceActive ? (geofenceName || 'Geofencing') : 'Geofence Configuration'}
              </h1>
              <p className="header-subtitle">
                {isGeofenceActive ? 'Monitor and manage geographic boundaries' : 'Setup your initial geofence name to begin'}
              </p>
            </div>
          </div>
          {isGeofenceActive && (
            <button className="add-entity-btn" onClick={() => {
              showGeofenceAlert({
                title: 'Add Entity',
                text: 'Click anywhere on the map to add a new geofence entity.',
                icon: 'info',
                confirmButtonColor: '#4318ff'
              });
            }}>
              <Plus size={18} />
              <span>Add Entity</span>
            </button>
          )}
        </div>

        {!isGeofenceActive ? (
          <div className="empty-state">
            <div className="empty-icon">
              <Navigation size={48} />
            </div>
            <h2>No Geofences Found</h2>
            <p>You haven't set up any geofences yet. Start by creating a name for your geofencing system.</p>
            <button className="create-init-btn" onClick={() => setIsNameModalOpen(true)}>
              <Plus size={20} />
              Create Geofence
            </button>
          </div>
        ) : (
          <div className="geofence-content">
            {/* Sidebar */}
            <div className="geofence-sidebar">
              <div className="sidebar-search">
                <div className="search-input-wrapper">
                  <Search size={18} className="search-icon" />
                  <input 
                    type="text" 
                    placeholder="Search geofences..." 
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && <X size={16} className="clear-search" onClick={() => setSearch('')} />}
                </div>
              </div>
              
              <div className="geofence-list">
                <div className="list-count">
                  {filteredGeofences.length} {filteredGeofences.length === 1 ? 'Entity' : 'Entities'} found
                </div>
                {filteredGeofences.length > 0 ? (
                  filteredGeofences.map((g, idx) => (
                    <div key={idx} className="geofence-item-card" onClick={() => handleFlyTo(g)}>
                      <div className="item-header">
                        <span className="item-name">{g.name}</span>
                        <div className="item-actions">
                          <button onClick={(e) => { e.stopPropagation(); handleEditClick(idx); }}>
                            <Edit2 size={14} />
                          </button>
                          <button className="delete" onClick={(e) => { e.stopPropagation(); handleDeleteClick(g); }}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="item-details">
                        <div className="detail-row">
                          <span>Lat</span>
                          <strong>{parseFloat(g.lat).toFixed(4)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Lng</span>
                          <strong>{parseFloat(g.lon).toFixed(4)}</strong>
                        </div>
                        <div className="detail-row">
                          <span>Radius</span>
                          <strong>{(parseFloat(g.radius)/1000).toFixed(1)} km</strong>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-results">
                    <Info size={24} />
                    <p>No matches found</p>
                  </div>
                )}
              </div>
            </div>

            {/* Map */}
            <div className="geofence-map-wrapper">
              <div ref={mapContainerRef} className="map-container" />
              <div className="map-overlay-hint">
                <Layers size={14} />
                <span>Click on map to add</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Geofence Name Modal */}
      {isNameModalOpen && (
        <div className="geofence-modal-overlay">
          <div className="geofence-modal-content small">
            <div className="modal-header">
              <h2>Geofence System Name</h2>
              <button onClick={() => setIsNameModalOpen(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>System Name</label>
                <input 
                  type="text" 
                  value={geofenceName} 
                  onChange={(e) => setGeofenceName(e.target.value)}
                  placeholder="e.g. Regional Boundaries"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsNameModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={startGeofenceCreation}>Start Setup</button>
            </div>
          </div>
        </div>
      )}

      {/* Entity Modal */}
      {isModalOpen && (
        <div className="geofence-modal-overlay">
          <div className="geofence-modal-content">
            <div className="modal-header">
              <h2>{editingIndex !== null ? 'Edit Entity' : 'Add New Entity'}</h2>
              <button onClick={() => setIsModalOpen(false)}><X size={20} /></button>
            </div>
            <div className="modal-body">
              <div className="modal-grid">
                <div className="form-group full">
                  <label>Entity Name</label>
                  <input 
                    type="text" 
                    value={newEntity.name} 
                    onChange={(e) => setNewEntity({...newEntity, name: e.target.value})}
                    placeholder="e.g. Downtown Zone"
                  />
                </div>
                <div className="form-group">
                  <label>Latitude</label>
                  <input 
                    type="text" 
                    value={newEntity.lat} 
                    readOnly
                    className="readonly"
                  />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input 
                    type="text" 
                    value={newEntity.lon} 
                    readOnly
                    className="readonly"
                  />
                </div>
                <div className="form-group full">
                  <label>Radius (meters)</label>
                  <div className="radius-input">
                    <input 
                      type="number" 
                      value={newEntity.radius} 
                      onChange={(e) => setNewEntity({...newEntity, radius: e.target.value})}
                      min="5000"
                    />
                    <span className="unit">meters</span>
                  </div>
                  <p className="hint">Minimum radius allowed: 5000m (5km)</p>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsModalOpen(false)}>Cancel</button>
              <button className="btn-primary" onClick={saveEntity}>
                {editingIndex !== null ? 'Update Entity' : 'Add Entity'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Geofence;
